import { css } from "@mds/core";

export const selectCSS = css`
	/* 	###################################################################### */
	/**	Component tokens
				   Use the design tokens to set these component tokens.
			   */
	:host {
		--line-width: 1px;
		--line-style: var(--mds-line-border-base-style);
		--line-color: var(--mds-color-border-select);
		--radius: var(--mds-line-border-base-radius);

		--label-width: 1fr;
		--select-width: 2fr;
		--select-height: 3rem;
		--select-align: stretch;
		--text-color: var(--mds-color-text-heading);
		--background-color: var(--mds-color-background-input);

		--padding-vertical: var(--mds-spacing-1);
		--padding-horizontal: var(--mds-spacing-3);

		--grid-columns: var(--label-width) var(--select-width) auto;
		--grid-rows: repeat(auto-fit, auto);

		--grid-column: 1 / -1;
		--grid-row: 1;

		--label-row: 1;
		--label-column: 1 / -1;

		--select-row: 2;
		--select-column: 1 / -1;

		--message-row: 3;
		--message-column: 1 / -1;

		--last-row: 3;

		--icon-glyph: var(--mds-icon-glyphs-base-chevron-down);
	}

	/* 	###################################################################### */
	/**	select host rules
				  This is the main rule set.
			   */
	:host {
		//	outline-color: transparent !important;
		width: 100%;
	}

	.mds-select {
		position: relative;
		display: grid;
		grid-template-columns: var(--grid-columns);
		grid-template-rows: var(--grid-rows);
		place-items: center var(--select-align);
		flex-wrap: wrap;
		gap: var(--mds-spacing-2);
		width: 100%;
	}
	.mds-select::after {
		grid-column: var(--select-column);
		grid-row: var(--select-row);
		justify-self: end;
		text-align: center;
		height: var(--select-height);
		display: grid;
		place-content: center;
		font-family: var(--mds-icon-font-base-family);
		font-weight: normal;
		content: var(--icon-glyph);
		border-inline-start-width: var(--line-width);
		border-inline-start-style: var(--line-style);
		border-inline-start-color: var(--mds-line-border-base-color);
		aspect-ratio: 1 / 1;
		background-color: var(--mds-color-background-default);
		background-clip: content-box;
		color: var(--mds-color-text-inverse);
		pointer-events: none;
		z-index: 1;
	}
	:host(:not([label])) > .mds-select {
		display: flex;
	}

	:host(:not([label])) > .mds-select > * {
		flex: 1 1 auto;
	}

	.mds-select label {
		grid-column: var(--label-column);
		grid-row: var(--label-row);
		color: var(--text-color);
	}

	.mds-select select {
		display: inline-block;
		grid-column: var(--select-column);
		grid-row: var(--select-row);
		border-style: var(--line-style);
		border-width: var(--line-width);
		border-color: var(--line-color);
		border-radius: var(--radius);
		margin: 0;
		padding:
			var(--padding-vertical)
			calc(var(--padding-horizontal) + var(--select-height))
			var(--padding-vertical)
			var(--padding-horizontal);
		font-family: inherit;
		font-size: inherit;
		line-height: 1rem;
		color: var(--text-color);
		background-color: var(--background-color);
		appearance: none;
		-webkit-appearance: none;
		height: var(--select-height);
	}

	.mds-select select[disabled]:hover {
		cursor: not-allowed;
	}

	.mds-select .messages {
		grid-column: var(--message-column);
		grid-row: var(--message-row);
		max-width: 80ch;
	}

	.mds-select .messages > * {
		margin: var(--mds-spacing-2) 0;
	}

	.mds-select .messages > *:first-child {
		margin-top: 0;
	}

	.mds-select .messages > *:last-child {
		margin-bottom: 0;
	}

	.mds-select .message {
		color: var(--text-color);
	}

	.mds-select .error {
		color: var(--text-color);
	}

	.error:before {
		padding-inline-end: var(--mds-spacing-2);
		font-family: var(--mds-icon-font-base-family);
		content: var(--mds-icon-glyphs-base-attention);
	}

	/* 	###################################################################### */
	/**	Properties for the select */
	/* Layout */
	:host([position="left"]) .mds-select {
		--grid-columns: var(--label-width) var(--select-width);
		--grid-rows: repeat(auto-fill, auto);
	}

	@media (min-width: 576px) {
		:host([position="left"]) {
			--label-row: 1;
			--label-column: 1 / 2;

			--select-row: 1;
			--select-column: 2 / 3;

			--message-row: 2;
			--message-column: 2 / 3;
		}
	}

	select:only-child {
		--grid-column: 1 / -1;
	}

	/* Appearance */
	:host([ondark]) select {
		--line-color: var(--mds-color-border-input-inverse);
	}

	:host([ondark]) .mds-select::after {
		color: var(--mds-color-text-default);
		background-color: var(--mds-color-background-subtle);
	}

	:host([ondark]) label {
		--text-color: var(--mds-color-text-inverse);
	}

	:host([ondark]) .message {
		--text-color: var(--mds-color-text-inverse);
	}

	:host([invalid]) .error {
		--text-color: var(--mds-color-feedback-error);
	}

	:host([invalid][ondark]) .error {
		--text-color: var(--mds-color-feedback-error-inverse);
	}

	:host([invalid]) select {
		--line-color: var(--mds-color-feedback-error);
	}

	:host([invalid][ondark]) select {
		--line-color: var(--mds-color-feedback-error-inverse);
	}

	:host([disabled]) select {
		--text-color: var(--mds-color-text-disabled);
		--line-color: var(--mds-color-transparent);
		--background-color: var(--mds-color-background-disabled);
	}

	:host([disabled]) .mds-select::after {
		color: var(--mds-color-text-disabled);
		border-color: var(--mds-color-text-disabled);
		background-color: var(--mds-color-transparent);
	}

	:host([disabled][ondark]) select {
		--text-color: var(--mds-color-text-disabled-inverse);
		--line-color: var(--mds-color-transparent);
		--background-color: var(--mds-color-background-disabled-inverse);
	}

	:host([disabled][ondark]) .mds-select::after {
		color: var(--mds-color-text-disabled-inverse);
		background-color: var(--mds-color-transparentdisabled);
		border-color: var(--mds-color-text-disabled-inverse);
	}
`;
